import { addLoadingState, removeLoadingState } from "./spa-app-actions.js";
import { isBusinessError } from "../service-provider/function-utils.js";
import { getConfigForAction } from "./async-action-utils.js";
import { toastBus } from "@progressive-development/pd-page";
import { actions$, createEffect } from "mini-rx-store";
import { filter, map, tap } from "rxjs/operators";
//#region src/store/async-action-effects.ts
/**
* Generic Effect Creators for Async Action Groups
*
* These factory functions create reusable effects for common patterns:
* - Loading state management (show/hide loading indicators)
* - Success toast notifications
* - Error toast notifications
*
* Usage:
* ```typescript
* const asyncGroups = [loadProfile, saveProfile, loadEvents];
*
* export const loadingEffect = createLoadingEffect(asyncGroups, { modal: true });
* export const successToastEffect = createSuccessToastEffect(asyncGroups);
* export const errorToastEffect = createErrorToastEffect(asyncGroups);
* ```
*/
/**
* Creates an effect that adds loading state when async actions start.
*
* @param groups - Array of async action groups to monitor
* @param options - Loading state display options
* @returns Effect that dispatches addLoadingState actions
*
* @example
* ```typescript
* // Modal loading for specific groups
* export const modalLoadingEffect = createLoadingEffect(
*   [loadProfile, saveProfile],
*   { modal: true }
* );
*
* // Background loading for other groups
* export const bgLoadingEffect = createLoadingEffect(
*   [loadEvents, loadRequests],
*   { smallBackground: true }
* );
* ```
*/
function createLoadingEffect(groups, options = {}) {
	const startTypes = groups.map((g) => g.types.action);
	return createEffect(actions$.pipe(filter((action) => startTypes.includes(action.type)), map((action) => {
		const config = getConfigForAction(action, groups);
		return addLoadingState({
			actionKey: config?.actionKey || "unknown",
			loadingTxt: config?.loadingTxt || "Loading...",
			isLoading: true,
			modal: options.modal ?? config?.modal ?? false,
			smallBackground: options.smallBackground ?? config?.smallBackground ?? false,
			loaderStyle: config?.loaderStyle
		});
	})));
}
/**
* Creates an effect that removes loading state when async actions complete.
*
* @param groups - Array of async action groups to monitor
* @returns Effect that dispatches removeLoadingState actions
*
* @example
* ```typescript
* export const removeLoadingEffect = createRemoveLoadingEffect([
*   loadProfile, saveProfile, loadEvents, loadRequests
* ]);
* ```
*/
function createRemoveLoadingEffect(groups) {
	const completionTypes = groups.flatMap((g) => [g.types.success, g.types.fail]);
	return createEffect(actions$.pipe(filter((action) => completionTypes.includes(action.type)), map((action) => {
		const config = getConfigForAction(action, groups);
		return removeLoadingState(config?.actionKey || "unknown");
	})));
}
/**
* Creates an effect that shows success toast when async actions succeed.
* Only shows toast if successTxt is defined and non-empty.
*
* @param groups - Array of async action groups to monitor
* @param options - Toast display options
* @returns Non-dispatching effect (uses toastBus directly)
*
* @example
* ```typescript
* export const successToastEffect = createSuccessToastEffect([
*   saveProfile,  // Has successTxt: "Profile saved!"
*   loadProfile,  // Has successTxt: "" (no toast)
* ]);
* ```
*/
function createSuccessToastEffect(groups, options = {}) {
	const successTypes = groups.filter((g) => g.config.successTxt).map((g) => g.types.success);
	return createEffect(actions$.pipe(filter((action) => successTypes.includes(action.type)), tap((action) => {
		const config = getConfigForAction(action, groups);
		if (config?.successTxt) {
			const duration = config.successDuration || options.defaultDuration || 4e3;
			toastBus.success(config.successTxt, duration);
		}
	})), { dispatch: false });
}
/**
* Creates an effect that shows error toast when async actions fail.
* Handles BusinessError to show appropriate error messages.
*
* @param groups - Array of async action groups to monitor
* @param options - Error handling options
* @returns Non-dispatching effect (uses toastBus directly)
*
* @example
* ```typescript
* export const errorToastEffect = createErrorToastEffect(
*   [loadProfile, saveProfile],
*   {
*     getErrorMessage: (code) => {
*       const messages: Record<number, string> = {
*         [-150]: "Access denied",
*         [-180]: "Technical error",
*       };
*       return messages[code] || "An error occurred";
*     },
*   }
* );
* ```
*/
function createErrorToastEffect(groups, options = {}) {
	const failTypes = groups.map((g) => g.types.fail);
	const defaultMessage = options.defaultErrorMessage || "An error occurred";
	const duration = options.duration ?? -1;
	return createEffect(actions$.pipe(filter((action) => failTypes.includes(action.type)), tap((action) => {
		const error = action.payload;
		let message;
		if (isBusinessError(error)) message = options.getErrorMessage ? options.getErrorMessage(error.code) : error.message || defaultMessage;
		else if (error instanceof Error) message = error.message || defaultMessage;
		else message = defaultMessage;
		toastBus.error(message, duration);
	})), { dispatch: false });
}
/**
* Creates all standard loading-related effects at once.
* Returns an object with all effects that can be spread into the effects array.
*
* @example
* ```typescript
* const {
*   modalLoadingEffect,
*   bgLoadingEffect,
*   removeLoadingEffect,
*   successToastEffect,
*   errorToastEffect
* } = createAllLoadingEffects({
*   modalGroups: [loadProfile, saveProfile],
*   backgroundGroups: [loadEvents],
*   successToastGroups: [saveProfile],
*   errorGroups: [loadProfile, saveProfile, loadEvents],
*   getErrorMessage: myErrorMessageFn,
* });
*
* // In store config:
* effects: [
*   modalLoadingEffect,
*   bgLoadingEffect,
*   removeLoadingEffect,
*   successToastEffect,
*   errorToastEffect,
*   // ... other effects
* ]
* ```
*/
function createAllLoadingEffects(options) {
	const allGroups = [...options.modalGroups || [], ...options.backgroundGroups || []];
	return {
		modalLoadingEffect: options.modalGroups?.length ? createLoadingEffect(options.modalGroups, { modal: true }) : null,
		bgLoadingEffect: options.backgroundGroups?.length ? createLoadingEffect(options.backgroundGroups, { smallBackground: true }) : null,
		removeLoadingEffect: allGroups.length ? createRemoveLoadingEffect(allGroups) : null,
		successToastEffect: options.successToastGroups?.length ? createSuccessToastEffect(options.successToastGroups) : null,
		errorToastEffect: options.errorGroups?.length ? createErrorToastEffect(options.errorGroups, {
			getErrorMessage: options.getErrorMessage,
			defaultErrorMessage: options.defaultErrorMessage
		}) : null
	};
}
//#endregion
export { createAllLoadingEffects, createErrorToastEffect, createLoadingEffect, createRemoveLoadingEffect, createSuccessToastEffect };
